<template>
  <div class="floating-header-container">
    <div 
      class="floating-header"
      :class="responsiveClasses"
      :style="headerStyles"
    >
      <!-- Icon Container with Gradient -->
      <div 
        class="icon-container"
        :style="iconContainerStyles"
      >
        <svg 
          class="icon"
          :class="iconSizeClass"
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            d="M12 2L2 7L12 12L22 7L12 2Z" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round"
          />
          <path 
            d="M2 17L12 22L22 17" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round"
          />
          <path 
            d="M2 12L12 17L22 12" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <!-- Title and Subtitle -->
      <div class="header-content">
        <h1 
          class="title"
          :class="titleSizeClass"
          :style="titleStyles"
        >
          {{ title }}
        </h1>
        <p 
          class="subtitle"
          :class="subtitleSizeClass"
          :style="subtitleStyles"
        >
          {{ subtitle }}
        </p>
      </div>
    </div>

    <!-- Site/Project Toggle -->
    <div class="header-controls">
      <SiteProjectToggle
        :visible-site-count="visibleSiteCount"
        :total-site-count="totalSiteCount"
        :projects="projects"
        @mode-changed="onModeChanged"
        @project-selected="onProjectSelected"
      />
    </div>
  </div>
</template>

<script>
import { BREAKPOINTS, Z_INDEX } from './constants.js';
import SiteProjectToggle from './SiteProjectToggle.vue';

export default {
  name: 'FloatingHeader',

  components: {
    SiteProjectToggle
  },

  props: {
    title: {
      type: String,
      default: 'CRESiteIQ™ GIS'
    },
    subtitle: {
      type: String,
      default: 'Geographic Intelligence System'
    },
    visibleSiteCount: {
      type: Number,
      default: 0
    },
    totalSiteCount: {
      type: Number,
      default: 0
    },
    projects: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      windowWidth: window.innerWidth
    };
  },

  watch: {
    projects: {
      handler(newVal) {
        console.log('FloatingHeader - projects prop changed:', newVal);
      },
      immediate: true,
      deep: true
    }
  },

  computed: {
    /**
     * Determine if the current viewport is mobile
     */
    isMobile() {
      return this.windowWidth < BREAKPOINTS.MOBILE;
    },

    /**
     * Determine if the current viewport is tablet
     */
    isTablet() {
      return this.windowWidth >= BREAKPOINTS.MOBILE && this.windowWidth < BREAKPOINTS.TABLET;
    },

    /**
     * Determine if the current viewport is desktop
     */
    isDesktop() {
      return this.windowWidth >= BREAKPOINTS.TABLET;
    },

    /**
     * Responsive classes based on viewport size
     */
    responsiveClasses() {
      return {
        'floating-header--mobile': this.isMobile,
        'floating-header--tablet': this.isTablet,
        'floating-header--desktop': this.isDesktop
      };
    },

    /**
     * Dynamic styles for the header container
     */
    headerStyles() {
      return {
        backgroundColor: 'var(--background)',
        borderColor: 'var(--border)',
        color: 'var(--foreground)',
        zIndex: Z_INDEX.FLOATING_HEADER
      };
    },

    /**
     * Gradient styles for the icon container
     */
    iconContainerStyles() {
      return {
        background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)'
      };
    },

    /**
     * Title color styles
     */
    titleStyles() {
      return {
        color: 'var(--foreground)'
      };
    },

    /**
     * Subtitle color styles
     */
    subtitleStyles() {
      return {
        color: 'var(--muted-foreground)'
      };
    },

    /**
     * Icon size class based on viewport
     */
    iconSizeClass() {
      if (this.isMobile) return 'w-4 h-4';
      if (this.isTablet) return 'w-5 h-5';
      return 'w-6 h-6';
    },

    /**
     * Title size class based on viewport
     */
    titleSizeClass() {
      if (this.isMobile) return 'text-xs';
      if (this.isTablet) return 'text-sm';
      return 'text-sm';
    },

    /**
     * Subtitle size class based on viewport
     */
    subtitleSizeClass() {
      if (this.isMobile) return 'text-[10px]';
      if (this.isTablet) return 'text-xs';
      return 'text-xs';
    }
  },

  mounted() {
    // Add resize listener for responsive behavior
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    // Clean up resize listener
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    /**
     * Handle window resize events
     */
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    /**
     * Handle mode change event from SiteProjectToggle
     */
    onModeChanged(mode) {
      this.$emit('mode-changed', mode);
    },

    /**
     * Handle project selection event from SiteProjectToggle
     */
    onProjectSelected(projectId) {
      this.$emit('project-selected', projectId);
    }
  }
};
</script>

<style scoped>
.floating-header-container {
  position: absolute;
  top: 1.5rem; /* top-6 */
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem; /* gap-3 */
  pointer-events: auto;
  z-index: 1000;
}

.floating-header {
  display: flex;
  align-items: center;
  gap: 0.75rem; /* gap-3 */
  padding: 0.75rem 1.5rem; /* py-3 px-6 */
  border-radius: 9999px; /* rounded-full */
  border-width: 2px;
  border-style: solid;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  background-color: rgba(255, 255, 255, 0.95);
  transition: all 200ms ease-out;
}

.header-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  position: relative;
}

/* Dark mode support - background opacity adjustment */
:root[data-theme="dark"] .floating-header {
  background-color: rgba(0, 0, 0, 0.95);
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem; /* w-10 */
  height: 2.5rem; /* h-10 */
  border-radius: 9999px; /* rounded-full */
  flex-shrink: 0;
  color: white;
}

.icon {
  color: white;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.125rem; /* gap-0.5 */
  line-height: 1.2;
}

.title {
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
}

.subtitle {
  margin: 0;
  white-space: nowrap;
  opacity: 0.8;
}

/* Responsive adjustments */
.floating-header-container {
  top: 1rem; /* top-4 on mobile */
}

.floating-header--mobile {
  padding: 0.5rem 1rem; /* py-2 px-4 */
  gap: 0.5rem; /* gap-2 */
}

.floating-header--mobile .icon-container {
  width: 2rem; /* w-8 */
  height: 2rem; /* h-8 */
}

.floating-header--tablet {
  padding: 0.625rem 1.25rem; /* py-2.5 px-5 */
  gap: 0.625rem; /* gap-2.5 */
}

.floating-header--tablet .icon-container {
  width: 2.25rem; /* w-9 */
  height: 2.25rem; /* h-9 */
}

/* Hover effect */
.floating-header:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Focus-visible for accessibility */
.floating-header:focus-within {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

/* Animation for initial load */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.floating-header-container {
  animation: fadeInDown 400ms ease-out;
}
</style>
